<template>
  <div ref="register">
    <div
      role="tablist"
      aria-label="Register"
      class="no-scrollbar mb-12 flex space-x-4 s:mb-8 s:overflow-x-auto s:px-5"
    >
      <button
        v-for="(tab, index) in tabs"
        :key="tab.value"
        type="button"
        role="tab"
        class="tab"
        :class="[{ 'dark-mode': props.dark, active: activeTab === tab.value }]"
        :id="`tab-${tab.value}`"
        :aria-selected="activeTab === tab.value"
        :aria-controls="`register-${tab.value}`"
        :tabindex="activeTab === tab.value ? 0 : -1"
        @click="changeTab(tab.value, tab.label)"
        @keydown="onTabKeydown($event, index)"
      >
        {{ tab.label }}
      </button>
    </div>

    <slot />
  </div>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { trackEvent } from '../utils/tracking';

const props = defineProps({
  tabs: {
    type: Array as () => Array<{ label: string; value: string }>,
    required: true,
  },
  dark: {
    type: Boolean,
    default: false,
  },
});

const register = ref<HTMLElement | null>(null);
const activeTab = ref('');

const changeTab = (value: string, label?: string) => {
  const element = register.value;
  if (!element) return;

  if (label) {
    trackEvent('Register', 'Select Tab', label);
  }

  activeTab.value = value;

  // The panels are server-rendered slot content outside Vue's render tree, so
  // they are shown/hidden imperatively. `inert` keeps the hidden panels out of
  // the tab order and the accessibility tree.
  element.querySelectorAll('.tab-content').forEach((panel) => {
    const isActive = panel.id === `register-${value}`;
    panel.classList.toggle('active', isActive);
    panel.toggleAttribute('inert', !isActive);
  });
};

const onTabKeydown = (event: KeyboardEvent, index: number) => {
  const tabCount = props.tabs.length;
  let nextIndex: number | null = null;

  if (event.key === 'ArrowRight') {
    nextIndex = (index + 1) % tabCount;
  } else if (event.key === 'ArrowLeft') {
    nextIndex = (index - 1 + tabCount) % tabCount;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = tabCount - 1;
  }

  if (nextIndex === null) return;
  event.preventDefault();

  const nextTab = props.tabs[nextIndex];
  changeTab(nextTab.value, nextTab.label);
  register.value
    ?.querySelector<HTMLButtonElement>(`#tab-${nextTab.value}`)
    ?.focus();
};

onMounted(() => {
  if (props.tabs.length > 0) {
    changeTab(props.tabs[0].value);
  }
});
</script>
